import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import useSyncMutation from './useSyncMutation';

import {
  EJECT_BATTERY_BAD_STATUS_CODE,
  FEATURE_FLAGS,
  QUERY_KEY,
  REDIRECT_BAD_STATUS_PAGE,
} from '~common';
import { useNavigateInApp } from '~hooks';
import { useLanguageContext } from '~providers';
import { postRequest } from '~services';
import { BatteryRequest, MutateContext } from '~types';
import { checkEnabledFeatureFlag } from '~utils';
import {
  getSubscriptionErrorCode,
  getSubscriptionErrorMessage,
  SubscriptionErrorCode,
} from '~utils/subscription-error.util';

interface Props extends MutateContext {
  onPaymentError?: (error: AxiosError) => void;
}

const getRequestBatteryConfig = (
  batteryRequest: BatteryRequest,
  isUnified: boolean,
) => {
  if (isUnified) {
    return {
      path: 'api/rental/request-battery-app',
      data: { ...batteryRequest, platform: 'WEB' },
    };
  }

  return {
    path: 'api/rental/request-battery-web',
    data: batteryRequest,
  };
};

export const useMutationRequestBattery = ({
  shouldToast = true,
  onPaymentError,
}: Props) => {
  const { lt } = useLanguageContext();
  const navigate = useNavigateInApp();
  const queryClient = useQueryClient();

  const useUnifiedRequestBatteryApiFeature = checkEnabledFeatureFlag(
    FEATURE_FLAGS.CLIENT_USE_UNIFIED_REQUEST_BATTERY_API,
  );

  return useSyncMutation(
    (batteryRequest: BatteryRequest) => {
      const { path, data } = getRequestBatteryConfig(
        batteryRequest,
        useUnifiedRequestBatteryApiFeature,
      );

      return postRequest({
        path,
        data,
      });
    },
    {
      onError(error) {
        if (error instanceof AxiosError) {
          let redirectPage = null;
          const errorCode = error.response?.data?.code;

          if (errorCode) {
            redirectPage = REDIRECT_BAD_STATUS_PAGE[errorCode];
            if (redirectPage) {
              navigate(redirectPage);
            }
          }

          // Subscription and pass errors all carry `code: -1`, so they are only
          // tellable apart by `string_code`.
          const subscriptionMessage = getSubscriptionErrorMessage(error, lt);

          if (!redirectPage && subscriptionMessage) {
            // The user already holds a pass, so the pricing options this screen
            // was showing are stale. Refetching moves the flow to the swap step
            // on its own rather than leaving them on a dead purchase button.
            // Both sources, as on success: with the pass flag off the flow reads
            // by-phone-number, and `/current` is disabled.
            if (
              getSubscriptionErrorCode(error) ===
              SubscriptionErrorCode.SUBSCRIPTION_ALREADY_EXISTED
            ) {
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEY.GET_USER_SUBSCRIPTION_BY_PHONE_NUMBER],
              });
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEY.GET_CURRENT_SUBSCRIPTION],
              });
            }

            if (shouldToast) {
              toast.error(subscriptionMessage);
            }
          } else if (!redirectPage) {
            if (
              error.response?.data?.message.includes('card_declined') ||
              errorCode === EJECT_BATTERY_BAD_STATUS_CODE.PRE_AUTH_FAILED
            ) {
              const message = lt(
                'text.hooks.useMutationRequestBattery.error',
                'Your card was declined',
              );
              // A caller with its own payment-error UI (the checkout's dialog)
              // takes the decline; everyone else gets the toast.
              if (onPaymentError) {
                onPaymentError(error);
              } else if (shouldToast) {
                toast.error(message);
              }
            } else {
              const message =
                error?.response?.data?.message ??
                lt(
                  'text.errorRequestBattery',
                  'Something went wrong when request battery',
                );

              if (shouldToast) {
                toast.error(message);
              }
            }
          }
        } else {
          if (shouldToast) {
            toast.error(
              lt(
                'text.errorRequestBattery',
                'Something went wrong when request battery',
              ),
            );
          }
        }
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY.GET_USER_SUBSCRIPTION_BY_PHONE_NUMBER],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY.GET_CURRENT_SUBSCRIPTION],
        });
      },
    },
  );
};
